import React, { useState, useEffect } from 'react';
import type { MLAlgorithm } from './Algorithm';
import { saiFilesApi } from '../services/api';
import brainIcon from '../assets/brain.svg';

interface TrainingProps {
  algorithm: MLAlgorithm;
  saiFileId?: number;
  config: any;
  onBack: () => void;
  onViewResults: (trainingResult?: any) => void;
  onTrainingInProgress?: (inProgress: boolean) => void;
}

export const Training: React.FC<TrainingProps> = ({ algorithm, saiFileId, config, onBack, onViewResults, onTrainingInProgress }) => {
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trainingResult, setTrainingResult] = useState<any>(null);
  const hasTrainedRef = React.useRef(false);

  useEffect(() => {
    // Prevent double training in React StrictMode
    if (hasTrainedRef.current) {
      return;
    }

    const buildTrainParams = (): any => {
      const baseParams = {
        type: algorithm,
        test_train_data_split: parseFloat(config.testTrainSplit), // Send as percentage integer (backend may convert to decimal)
        scaler: config.scaler,
      };

      switch (algorithm) {
        case 'random forest':
          return {
            ...baseParams,
            n_estimators: parseInt(config.nEstimators, 10),
            max_depth: parseInt(config.maxDepth, 10),
          };
        case 'support vector':
          return {
            ...baseParams,
            kernel: config.kernel,
            c: parseFloat(config.c),
            degree: config.kernel === 'poly' && config.degree ? parseInt(config.degree, 10) : 0,
          };
        case 'neural network':
          return {
            ...baseParams,
            num_layers: parseInt(config.numLayers, 10),
            num_neurons: parseInt(config.numNodes, 10),
            epochs: parseInt(config.epochs, 10),
            learning_rate: parseFloat(config.learningRate),
            activation_function: 'relu',
          };
        case 'gradient boosting':
          // Add gradient boosting specific params when implemented
          return baseParams;
        default:
          return baseParams;
      }
    };

    const trainModel = async () => {
      if (!saiFileId || !config) {
        setError('Missing SAI file ID or configuration');
        setIsComplete(true);
        return;
      }

      hasTrainedRef.current = true;
      let progressInterval: ReturnType<typeof setInterval> | undefined;
      try {
        // Start progress simulation
        progressInterval = setInterval(() => {
          setProgress((prev) => {
            if (prev >= 90) {
              return 90; // Stop at 90% until API call completes
            }
            return prev + 10;
          });
        }, 200);

        // Call the train API
        const params = buildTrainParams();
        const result = await saiFilesApi.train(saiFileId, params);

        clearInterval(progressInterval);
        setTrainingResult(result);
        setProgress(100);
        setIsComplete(true);
      } catch (err: any) {
        if (progressInterval) clearInterval(progressInterval);
        setError(err.response?.data?.error || err.message || 'Failed to train model');
        setProgress(100);
        setIsComplete(true);
      }
    };

    trainModel();
  }, [saiFileId, config, algorithm]);

  useEffect(() => {
    onTrainingInProgress?.(progress < 100);
    return () => onTrainingInProgress?.(false);
  }, [progress, onTrainingInProgress]);

  const getAlgorithmTitle = () => {
    switch (algorithm) {
      case 'random forest':
        return 'Random Forest';
      case 'support vector':
        return 'Support Vector Machine';
      case 'neural network':
        return 'Neural Network';
      case 'gradient boosting':
        return 'Gradient Boosting';
      default:
        return 'ML Algorithm';
    }
  };

  return (
    <div className="wizard-step-container">
      <div className="wizard-step-header">
        <button className="back-button" onClick={onBack} disabled={!isComplete}>
          <span className="back-arrow">←</span> Back
        </button>
        <h1>Training {getAlgorithmTitle()} Model</h1>
      </div>

      <div className="training-content">
        <div className="training-icon">
          <img src={brainIcon} alt="AI Brain" width="120" height="120" />
        </div>

        <p className="training-message">
          Training your custom AI model on the selected dataset. This may take a few minutes...
        </p>

        <div className="progress-bar-container">
          <div className="progress-bar">
            <div
              className="progress-bar-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <p className="training-progress-text">
          Training Progress: {progress}% Complete
        </p>

        {error && (
          <div className="error-message" style={{ color: 'red', marginBottom: '1rem', padding: '0.5rem' }}>
            {error}
          </div>
        )}

        <button
          className="btn btn-primary continue-btn-full"
          onClick={() => onViewResults(trainingResult)}
          disabled={!isComplete || !!error}
        >
          View Training Results
          <span className="arrow-right">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M4.16699 10H15.8337M15.8337 10L10.0003 4.16669M15.8337 10L10.0003 15.8334" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
        </button>
      </div>
    </div>
  );
};

